import { create } from 'zustand';
import type {
  ApprovalRequest,
  BrowserTab,
  FileEntry,
  Lane,
  LaneLogLine,
  LaneRole,
  LaneStatus,
  ProviderKind,
  Receipt,
  SelectedElement,
  SwarmEvent,
  SwarmEventKind,
  Workspace,
} from './types.js';

let tickCounter = 0;
function nextId(prefix: string): string {
  tickCounter += 1;
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}${tickCounter.toString(36)}${rand}`;
}

export interface CreateWorkspaceInput {
  name: string;
  path: string;
  goal: string;
  lanes: Array<{
    role: LaneRole;
    provider: ProviderKind;
    model: string;
    task: string;
  }>;
}

export interface MissionControlState {
  workspaces: Workspace[];
  lanes: Lane[];
  events: SwarmEvent[];
  approvals: ApprovalRequest[];
  receipts: Receipt[];
  browserTabs: BrowserTab[];
  activeWorkspaceId: string | null;
  activeLaneId: string | null;
  activeTabId: string | null;
  fileTree: FileEntry[];
  selectedFilePath: string | null;

  createWorkspace: (input: CreateWorkspaceInput) => Workspace;
  removeWorkspace: (id: string) => void;
  setActiveWorkspace: (id: string | null) => void;
  setActiveLane: (id: string | null) => void;

  addLane: (
    workspaceId: string,
    data: { role: LaneRole; provider: ProviderKind; model: string; task: string },
  ) => Lane;
  updateLane: (id: string, updates: Partial<Lane>) => void;
  appendLaneLog: (id: string, line: Omit<LaneLogLine, 'at'>) => void;
  setLaneStatus: (id: string, status: LaneStatus) => void;

  emitEvent: (event: Omit<SwarmEvent, 'id' | 'at'> & { at?: number }) => SwarmEvent;

  requestApproval: (data: Omit<ApprovalRequest, 'id' | 'createdAt' | 'status'>) => ApprovalRequest;
  resolveApproval: (id: string, approved: boolean) => void;

  addReceipt: (data: Omit<Receipt, 'id' | 'createdAt'>) => Receipt;

  openBrowserTab: (workspaceId: string, url: string) => BrowserTab;
  closeBrowserTab: (id: string) => void;
  setActiveTab: (id: string | null) => void;
  updateBrowserTab: (id: string, updates: Partial<BrowserTab>) => void;
  setSelectedElement: (tabId: string, element: SelectedElement | undefined) => void;

  setFileTree: (tree: FileEntry[]) => void;
  setSelectedFile: (path: string | null) => void;

  reset: () => void;
}

function appendLog(lane: Lane, line: Omit<LaneLogLine, 'at'>): Lane {
  const next: Lane = {
    ...lane,
    logs: [...lane.logs, { ...line, at: Date.now() }].slice(-50),
    lastUpdated: Date.now(),
  };
  return next;
}

export const useMissionControlStore = create<MissionControlState>((set) => ({
  workspaces: [],
  lanes: [],
  events: [],
  approvals: [],
  receipts: [],
  browserTabs: [],
  activeWorkspaceId: null,
  activeLaneId: null,
  activeTabId: null,
  fileTree: [],
  selectedFilePath: null,

  createWorkspace: (input) => {
    const workspaceId = nextId('ws');
    const now = Date.now();

    const lanes: Lane[] = input.lanes.map((lane) => ({
      id: nextId('lane'),
      workspaceId,
      role: lane.role,
      name: `${lane.role.charAt(0).toUpperCase()}${lane.role.slice(1)} Lane`,
      provider: lane.provider,
      model: lane.model,
      status: 'idle',
      currentTask: lane.task,
      logs: [],
      lastUpdated: now,
    }));

    const coordinator = lanes.find((lane) => lane.role === 'coordinator') ?? null;

    const workspace: Workspace = {
      id: workspaceId,
      name: input.name,
      path: input.path,
      createdAt: now,
      laneIds: lanes.map((lane) => lane.id),
      coordinatorId: coordinator?.id ?? null,
      activeGoal: input.goal,
    };

    set((state) => ({
      workspaces: [...state.workspaces, workspace],
      lanes: [...state.lanes, ...lanes],
      activeWorkspaceId: workspace.id,
      activeLaneId: coordinator?.id ?? lanes[0]?.id ?? null,
    }));

    return workspace;
  },

  removeWorkspace: (id) =>
    set((state) => ({
      workspaces: state.workspaces.filter((ws) => ws.id !== id),
      lanes: state.lanes.filter((lane) => lane.workspaceId !== id),
      events: state.events.filter((event) => event.workspaceId !== id),
      approvals: state.approvals.filter((approval) => approval.workspaceId !== id),
      receipts: state.receipts.filter((receipt) => receipt.workspaceId !== id),
      browserTabs: state.browserTabs.filter((tab) => tab.workspaceId !== id),
      activeWorkspaceId: state.activeWorkspaceId === id ? null : state.activeWorkspaceId,
      activeLaneId:
        state.lanes.find((lane) => lane.id === state.activeLaneId)?.workspaceId === id
          ? null
          : state.activeLaneId,
    })),

  setActiveWorkspace: (id) => {
    set((state) => {
      if (id === null) {
        return { activeWorkspaceId: null, activeLaneId: null };
      }
      const workspace = state.workspaces.find((ws) => ws.id === id);
      const fallbackLaneId = workspace?.coordinatorId ?? workspace?.laneIds[0] ?? null;
      return { activeWorkspaceId: id, activeLaneId: fallbackLaneId };
    });
  },

  setActiveLane: (id) => set({ activeLaneId: id }),

  addLane: (workspaceId, data) => {
    const lane: Lane = {
      id: nextId('lane'),
      workspaceId,
      role: data.role,
      name: `${data.role.charAt(0).toUpperCase()}${data.role.slice(1)} Lane`,
      provider: data.provider,
      model: data.model,
      status: 'idle',
      currentTask: data.task,
      logs: [],
      lastUpdated: Date.now(),
    };

    set((state) => ({
      lanes: [...state.lanes, lane],
      workspaces: state.workspaces.map((ws) =>
        ws.id === workspaceId ? { ...ws, laneIds: [...ws.laneIds, lane.id] } : ws,
      ),
    }));

    return lane;
  },

  updateLane: (id, updates) =>
    set((state) => ({
      lanes: state.lanes.map((lane) =>
        lane.id === id ? { ...lane, ...updates, lastUpdated: Date.now() } : lane,
      ),
    })),

  appendLaneLog: (id, line) =>
    set((state) => ({
      lanes: state.lanes.map((lane) => (lane.id === id ? appendLog(lane, line) : lane)),
    })),

  setLaneStatus: (id, status) =>
    set((state) => ({
      lanes: state.lanes.map((lane) =>
        lane.id === id ? { ...lane, status, lastUpdated: Date.now() } : lane,
      ),
    })),

  emitEvent: (event) => {
    const record: SwarmEvent = {
      id: nextId('evt'),
      at: event.at ?? Date.now(),
      workspaceId: event.workspaceId,
      laneId: event.laneId,
      kind: event.kind,
      summary: event.summary,
      payload: event.payload,
    };
    set((state) => ({ events: [record, ...state.events].slice(0, 200) }));
    return record;
  },

  requestApproval: (data) => {
    const record: ApprovalRequest = {
      ...data,
      id: nextId('appr'),
      createdAt: Date.now(),
      status: 'pending',
    };
    set((state) => ({
      approvals: [...state.approvals, record],
      lanes: state.lanes.map((lane) =>
        lane.id === data.laneId ? { ...lane, status: 'waiting_approval', lastUpdated: Date.now() } : lane,
      ),
    }));
    return record;
  },

  resolveApproval: (id, approved) =>
    set((state) => {
      const target = state.approvals.find((a) => a.id === id);
      if (!target || target.status !== 'pending') {
        return {};
      }
      const nextStatus: ApprovalRequest['status'] = approved ? 'approved' : 'rejected';
      return {
        approvals: state.approvals.map((a) =>
          a.id === id ? { ...a, status: nextStatus, resolvedAt: Date.now() } : a,
        ),
        lanes: state.lanes.map((lane) =>
          lane.id === target.laneId
            ? {
                ...lane,
                status: approved ? 'running' : 'review',
                lastUpdated: Date.now(),
                logs: [
                  ...lane.logs,
                  {
                    at: Date.now(),
                    text: approved ? `Approved: ${target.title}` : `Rejected: ${target.title}`,
                    tone: approved ? 'good' : 'warn',
                  },
                ].slice(-50),
              }
            : lane,
        ),
      };
    }),

  addReceipt: (data) => {
    const record: Receipt = {
      ...data,
      id: nextId('rcpt'),
      createdAt: Date.now(),
    };
    set((state) => ({ receipts: [record, ...state.receipts].slice(0, 50) }));
    return record;
  },

  openBrowserTab: (workspaceId, url) => {
    const tab: BrowserTab = {
      id: nextId('tab'),
      workspaceId,
      url,
      title: url.replace(/^https?:\/\//, '').slice(0, 40) || 'New Tab',
      status: 'loading',
    };
    set((state) => ({
      browserTabs: [...state.browserTabs, tab],
      activeTabId: tab.id,
    }));
    return tab;
  },

  closeBrowserTab: (id) =>
    set((state) => {
      const remaining = state.browserTabs.filter((tab) => tab.id !== id);
      return {
        browserTabs: remaining,
        activeTabId: state.activeTabId === id ? (remaining[0]?.id ?? null) : state.activeTabId,
      };
    }),

  setActiveTab: (id) => set({ activeTabId: id }),

  updateBrowserTab: (id, updates) =>
    set((state) => ({
      browserTabs: state.browserTabs.map((tab) => (tab.id === id ? { ...tab, ...updates } : tab)),
    })),

  setSelectedElement: (tabId, element) =>
    set((state) => ({
      browserTabs: state.browserTabs.map((tab) =>
        tab.id === tabId ? { ...tab, selectedElement: element } : tab,
      ),
    })),

  setFileTree: (tree) => set({ fileTree: tree }),

  setSelectedFile: (path) => set({ selectedFilePath: path }),

  reset: () =>
    set({
      workspaces: [],
      lanes: [],
      events: [],
      approvals: [],
      receipts: [],
      browserTabs: [],
      activeWorkspaceId: null,
      activeLaneId: null,
      activeTabId: null,
      fileTree: [],
      selectedFilePath: null,
    }),
}));

export function selectWorkspaceLanes(state: MissionControlState, workspaceId: string | null): Lane[] {
  if (!workspaceId) return [];
  return state.lanes.filter((lane) => lane.workspaceId === workspaceId);
}

export function selectWorkspaceEvents(state: MissionControlState, workspaceId: string | null): SwarmEvent[] {
  if (!workspaceId) return [];
  return state.events.filter((event) => event.workspaceId === workspaceId);
}

export function selectPendingApprovals(
  state: MissionControlState,
  workspaceId: string | null,
): ApprovalRequest[] {
  if (!workspaceId) return state.approvals.filter((a) => a.status === 'pending');
  return state.approvals.filter((a) => a.workspaceId === workspaceId && a.status === 'pending');
}

export function selectWorkspaceTabs(state: MissionControlState, workspaceId: string | null): BrowserTab[] {
  if (!workspaceId) return [];
  return state.browserTabs.filter((tab) => tab.workspaceId === workspaceId);
}

export type { LaneRole, LaneStatus, ProviderKind, SwarmEventKind };
